// contexts/AdminContext.ts
import { createContext, useContext } from 'react';
import type { AppUser } from '@/types/users';
import type { Agency } from '@/types/agencies';
import type { Broadcaster } from '@/types/broadcasters';
import type { Channel } from '@/types/channels';
import type { Country } from '@/types/countries';
import type { Taxonomy } from '@/types/taxonomies';
import type { Customer } from '@/types/customers';

export type AdminEntityType =
    | 'users'
    | 'agencies'
    | 'broadcasters'
    | 'clients'
    | 'channels'
    | 'unassociatedAgencies'
    | 'unassociatedClients'
    | 'countries'
    | 'taxonomies';

export type SaveableEntityType = 'agency' | 'broadcaster' | 'channel' | 'client' | 'user';
export type DeletableEntityType = 'user' | 'agency' | 'client';

export interface AdminEntityData {
    id?: number;
    existingId?: number;
    [key: string]: unknown;
}

export interface AdminData {
    users: AppUser[];
    agencies: Agency[];
    broadcasters: Broadcaster[];
    clients: Customer[];
    channels: Channel[];
    unassociatedAgencies: Agency[];
    unassociatedClients: Customer[];
    countries: Country[];
    taxonomies: Taxonomy[];
}

export interface AdminContextValue {
    adminData: AdminData;
    loading: boolean;
    fetchData: (entityType: AdminEntityType) => Promise<unknown>;
    fetchCompleteEntitySet: (entityType: string) => Promise<{ associated: unknown; unassociated: unknown } | undefined>;
    handleSaveEntity: (
        entityData: AdminEntityData,
        entityType: SaveableEntityType,
        onSuccess?: () => void
    ) => Promise<void>;
    handleDeleteEntity: (entityId: number, entityType: DeletableEntityType) => Promise<void>;
}

export const AdminContext = createContext<AdminContextValue | null>(null);

export const useAdmin = (): AdminContextValue => {
    const context = useContext(AdminContext);

    if (context === null) {
        throw new Error('useAdmin must be used within an AdminProvider');
    }

    return context;
};
